import React, { lazy, Suspense, useState } from 'react';
import Button from '@ajat/design-system/Button';
import { ColorSelector, LabelSelector, LargeLabelSelector } from '@ajat/design-system/Configurator';
import Input from '@ajat/design-system/Input';
import { ComponentPage, DemoCard, DemoRow } from '../shared';
import {
  THREE_D_TEMPLATES,
  DEFAULT_TEMPLATE_KEY,
} from '@ajat/design-system/Product3DPreview/templates';
import palette from '@ajat/design-system/Product3DPreview/config/palette.json';

/**
 * three.js and @react-three/fiber are a large dependency. The registry imports
 * every page eagerly, so importing the preview directly here would put the
 * whole 3D stack in the chunk for EVERY design system page. Lazy-loading it
 * keeps that cost on this page alone — the same pattern ThreeDGallery and
 * CreateSchool use.
 */
const Product3DPreview = lazy(() => import('@ajat/design-system/Product3DPreview'));

/** The garment's colours from config/palette.json — `tint` is the fabric colour, `swatch` the UI chip. */
const colorsOf = (template) => palette.colors?.[template.palette] || [];

const INKS = [
  { id: 'white', name: 'Hvid', swatch: '#FFFFFF' },
  { id: 'black', name: 'Sort', swatch: '#111111' },
];

const Loading = () => (
  <div
    style={{
      aspectRatio: '1 / 1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-gray-025)',
      borderRadius: 'var(--radius-card)',
    }}
  >
    <span className="typo-label" style={{ color: 'var(--color-gray-5)' }}>LOADING 3D…</span>
  </div>
);

export const meta = {
  title: 'Product 3D Preview',
  group: 'Commerce',
  importPath: '@ajat/design-system/Product3DPreview',
  description:
    'Interactive 3D garment preview. It projects the customer\'s prints onto the model as independent decal slots — chest text, front and back artwork, a shoulder flag and a class letter — each with its own lifecycle, so changing one never disturbs the others. Scene, materials and slot anchors are configured in JSON, not in code.',
  props: [
    ['modelUrl', 'string', 'null', 'GLB url. Null falls back to flat photo planes.'],
    ['modelConfig', 'object', 'hoodie-1', 'Anchors, slots, body textures and axes — templates/<key>/model.json'],
    ['basePhotos', 'object', 'null', '{ front, back } photos for the fallback planes'],
    ['bodyColor', 'string', "'#1B365D'", 'Fabric colour — pass the palette `tint`, not the UI `swatch`'],
    ['frontImage', 'string', 'null', 'Chest artwork url'],
    ['backImage', 'string', 'null', 'Back artwork url'],
    ['backArtworkScale', 'number', '1', 'Back print scale: 1 = A4, √2 = A3'],
    ['text', 'string', "'NAVNETRYK'", 'Chest label text'],
    ['textColor', 'string', "'#FFFFFF'", 'Ink of the chest label and the sleeve text'],
    ['artworkColor', 'string', 'null', 'Flattens uploaded artwork to one ink; null keeps it as uploaded'],
    ['flagImage', 'string', 'null', 'Right-shoulder flag artwork'],
    ['flagFinish', "'print' | 'embroidery'", "'print'", 'Flag finish'],
    ['letter', 'string', 'null', 'Left-shoulder class letter A–Z'],
    ['letterFinish', "'gloss' | 'embroidery'", "'gloss'", 'Letter finish'],
    ['specialText', 'string', 'null', 'Sleeve text, max 16 characters'],
    ['backName', 'string', 'null', 'Jersey back name (vogn-1), capitals, max 14 characters'],
    ['backNumber', 'string', 'null', "Jersey back number '0'–'99' (leading zero kept)"],
    ['backYear', 'string', 'null', "Jersey back line under the number ('STUDENT 2026' — the shop's season year)"],
    ['hiddenPrints', 'string[]', 'null', "Fixed prints left off, e.g. ['chestWordmark'] = the Vogntrøje's «Fjern logo»"],
    ['view', 'string', '—', "Controlled view: a key of the template's views.presets ('front' | 'back' | …); uncontrolled when omitted"],
    ['onViewChange', 'func', '—', 'Fires when the user toggles the view'],
    ['viewResetKey', 'any', '0', 'Change it to re-frame the camera'],
    ['enableRotate', 'bool', 'true', 'Drag to rotate'],
    ['showToggle', 'bool', 'false', 'Show the front / back toggle'],
    ['frontLabel', 'string', "'Forsiden'", 'Front toggle label'],
    ['backLabel', 'string', "'Bagsiden'", 'Back toggle label'],
    ['onPlacementPick', 'func', '—', 'Development aid: clicking the model reports a body-local anchor for model.json. Leave undefined in production.'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — it is transcribed from the JSDoc block above the component. It is also lazy-loaded here: three.js must not land in the bundle of every other page.',
  usage: [
    {
      title: 'Always lazy-load it',
      code: `import React, { lazy, Suspense } from 'react';

const Product3DPreview = lazy(() => import('@ajat/design-system/Product3DPreview'));

<Suspense fallback={<div className="pp__gallery-3d-loading" />}>
  <Product3DPreview
    modelUrl={template.modelUrl}
    modelConfig={template.modelConfig}
    bodyColor={color.tint}          // tint, not swatch
    text="NAVNETRYK"
    textColor={ink.swatch}
    artworkColor={ink.swatch}
  />
</Suspense>`,
    },
    {
      title: 'Decal slots are independent',
      code: `// Each slot has its own effect with its own deps in HoodieMesh.
// Never collapse them into one — deleting the back print would
// then rebuild the front print and the name print too.

useEffect(() => { /* frontArtwork */ }, [frontImage, artworkColor]);
useEffect(() => { /* backArtwork  */ }, [backImage, artworkColor]);
useEffect(() => { /* navnetryk    */ }, [text, textColor]);

// Body recolour must skip decals, and decal materials stay white:
mesh.traverse((obj) => {
  if (obj.isMesh && !obj.userData.__isDecal) obj.material.color.set(bodyColor);
});`,
    },
    {
      title: 'Calibrating slot placement',
      code: `// Positions live in the template's model.json, never in the
// projection effects. Capture new camera presets in the browser:
//   ?p3d=debug  →  window.p3d.getView()
//                  window.p3d.setView({ ... })`,
    },
  ],
};

export default function Product3DPreviewPage() {
  const [mounted, setMounted] = useState(false);
  const [templateKey, setTemplateKey] = useState(DEFAULT_TEMPLATE_KEY);
  const template = THREE_D_TEMPLATES[templateKey];
  const colors = colorsOf(template);
  const [colorId, setColorId] = useState(null);
  const color = colors.find((c) => c.id === colorId) || colors[0] || {};
  const [ink, setInk] = useState(INKS[0]);
  const [text, setText] = useState('NAVNETRYK');
  const [letter, setLetter] = useState(null);
  const [specialText, setSpecialText] = useState('');
  const [view, setView] = useState('front');
  // Vogntrøje (vogn-1): the jersey back and «Fjern logo»
  const slots = template.modelConfig?.slots || {};
  const [backName, setBackName] = useState('HANSEN');
  const [backNumber, setBackNumber] = useState('07');
  const [showYear, setShowYear] = useState(true);
  const [removeLogo, setRemoveLogo] = useState(false);
  const views = template.modelConfig?.views?.presets || [];
  const pickTemplate = (key) => { setTemplateKey(key); setColorId(null); setView('front'); };

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live preview" hint="drag to rotate · loads three.js on demand">
        {!mounted ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-s)', alignItems: 'flex-start' }}>
            <p className="typo-body-sm" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch' }}>
              The 3D stack is a large download, so it is not fetched until you ask for it — the same
              lazy boundary the product pages use.
            </p>
            <Button size="secondary" variant="main" onClick={() => setMounted(true)}>
              Load the 3D preview
            </Button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-ui-medium-m)' }}>
            {/* The preview fills its parent (height 100%): the parent gives it its shape */}
            <div style={{ maxWidth: 520, aspectRatio: '1 / 1' }}>
              <Suspense fallback={<Loading />}>
                <Product3DPreview
                  key={template.key}
                  modelUrl={template.modelUrl}
                  modelConfig={template.modelConfig}
                  bodyColor={color.tint}
                  text={text}
                  textColor={ink.swatch}
                  artworkColor={ink.swatch}
                  letter={letter}
                  specialText={specialText || null}
                  backName={backName}
                  backNumber={backNumber}
                  backYear={showYear ? 'STUDENT 2026' : null}
                  hiddenPrints={removeLogo ? ['chestWordmark'] : null}
                  view={view}
                  onViewChange={setView}
                  showToggle
                />
              </Suspense>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-s)' }}>
              <div>
                <span className="typo-body-sm" style={{ display: 'block', paddingBottom: 'var(--space-ui-small-m)', color: 'var(--ds-doc-ink-muted)' }}>
                  GARMENT — the template (layout key)
                </span>
                <DemoRow align="center">
                  {Object.values(THREE_D_TEMPLATES).map((t) => (
                    <LargeLabelSelector key={t.key} selected={t.key === templateKey} onClick={() => pickTemplate(t.key)}>
                      {t.label}
                    </LargeLabelSelector>
                  ))}
                </DemoRow>
              </div>

              <div>
                <span className="typo-body-sm" style={{ display: 'block', paddingBottom: 'var(--space-ui-small-m)', color: 'var(--ds-doc-ink-muted)' }}>
                  CAMERA — the template's views.presets (what the admin picks per attribute)
                </span>
                <DemoRow align="center">
                  {views.map((v) => (
                    <LargeLabelSelector key={v.key} selected={v.key === view} onClick={() => setView(v.key)}>
                      {v.label || v.key}
                    </LargeLabelSelector>
                  ))}
                </DemoRow>
              </div>

              <div>
                <span className="typo-body-sm" style={{ display: 'block', paddingBottom: 'var(--space-ui-small-m)', color: 'var(--ds-doc-ink-muted)' }}>
                  FABRIC — passes palette.tint ({template.palette})
                </span>
                <DemoRow align="center">
                  {colors.map((c) => (
                    <ColorSelector
                      key={c.id}
                      color={c.swatch}
                      name={c.name}
                      selected={c.id === color.id}
                      onClick={() => setColorId(c.id)}
                    />
                  ))}
                </DemoRow>
              </div>

              <div>
                <span className="typo-body-sm" style={{ display: 'block', paddingBottom: 'var(--space-ui-small-m)', color: 'var(--ds-doc-ink-muted)' }}>
                  INK — colours the label and the artwork
                </span>
                <DemoRow align="center">
                  {INKS.map((c) => (
                    <ColorSelector
                      key={c.id}
                      color={c.swatch}
                      name={c.name}
                      selected={c.id === ink.id}
                      onClick={() => setInk(c)}
                    />
                  ))}
                </DemoRow>
              </div>

              {slots.navnetryk && (
                <div style={{ maxWidth: 320 }}>
                  <Input label="Navnetryk" value={text} onChange={(e) => setText(e.target.value)} maxLength={16} />
                </div>
              )}

              {slots.specialText && (
                <div style={{ maxWidth: 320 }}>
                  <Input label="Speciel tekst (ærme)" value={specialText} onChange={(e) => setSpecialText(e.target.value)} maxLength={16} />
                </div>
              )}

              {slots.backName && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-ui-small-l)', alignItems: 'flex-end' }}>
                  <div style={{ width: 220 }}>
                    <Input label="Navn (ryg)" value={backName} onChange={(e) => setBackName(e.target.value)} maxLength={14} />
                  </div>
                  <div style={{ width: 120 }}>
                    <Input label="Nummer" value={backNumber} onChange={(e) => setBackNumber(e.target.value.replace(/\D/g, '').slice(0, 2))} />
                  </div>
                  <LargeLabelSelector selected={showYear} onClick={() => setShowYear((on) => !on)}>STUDENT 2026</LargeLabelSelector>
                  <LargeLabelSelector selected={removeLogo} onClick={() => setRemoveLogo((on) => !on)}>Fjern logo</LargeLabelSelector>
                </div>
              )}

              <div>
                <span className="typo-body-sm" style={{ display: 'block', paddingBottom: 'var(--space-ui-small-m)', color: 'var(--ds-doc-ink-muted)' }}>
                  CLASS LETTER — left shoulder
                </span>
                <DemoRow align="center">
                  {[null, 'A', 'B', 'C'].map((l) => (
                    <LabelSelector key={l ?? 'none'} selected={letter === l} onClick={() => setLetter(l)}>
                      {l ?? '–'}
                    </LabelSelector>
                  ))}
                </DemoRow>
              </div>
            </div>
          </div>
        )}
      </DemoCard>

      <DemoCard title="Templates" hint="registry keys the admin can pick as a layout" surface={false}>
        <div className="ds-page__table-scroll">
          <table className="ds-page__table">
            <thead>
              <tr>
                <th className="typo-label">Key</th>
                <th className="typo-label">Label</th>
                <th className="typo-label">Config</th>
              </tr>
            </thead>
            <tbody className="typo-body-sm">
              {Object.values(THREE_D_TEMPLATES).map((t) => (
                <tr key={t.key}>
                  <td><code className="ds-page__table-name">{t.key}</code></td>
                  <td className="ds-page__table-type">{t.label}</td>
                  <td><code className="ds-page__table-default">templates/{t.key}/model.json</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="typo-body" style={{ paddingTop: 'var(--space-ui-small-l)', color: 'var(--ds-doc-ink-muted)' }}>
          Keep this registry in step with the backend allowlist <code>config(&apos;app.three_d_templates&apos;)</code>.
        </p>
      </DemoCard>

      <DemoCard title="Rules worth knowing" surface={false}>
        <ul className="typo-body-sm" style={{ color: 'var(--color-gray-7)', maxWidth: '68ch', paddingLeft: '1.2em', listStyle: 'disc' }}>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Every decal slot gets its <strong>own</strong> effect and cleanup. A shared effect makes deleting
            one print rebuild the others.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Recolouring the body must skip <code>userData.__isDecal</code>, and decal
            materials stay white — otherwise the prints pick up the fabric tint.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Pass the palette <code>tint</code> as <code>bodyColor</code>,
            not the UI <code>swatch</code>.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Slot positions belong in <code>model.json</code>, never hardcoded in the
            projection effects.
          </li>
          <li>
            The GLB is Draco-compressed; decoder assets live in <code>public/draco/gltf/</code>,
            deliberately local rather than on a CDN.
          </li>
        </ul>
      </DemoCard>
    </ComponentPage>
  );
}
